import { cloneElement, type FunctionComponent, type SVGProps } from 'react'
import { Box, HStack, SimpleGrid, Text, Tooltip, VStack } from '@chakra-ui/react'
import StatCompletionIcon from 'assets/icons/stat-completion.svg?react'
import StatTeamsIcon from 'assets/icons/stat-teams.svg?react'
import StatTrainingIcon from 'assets/icons/stat-training.svg?react'
import StatUpcomingIcon from 'assets/icons/stat-upcoming.svg?react'
import {
  PLAY_DASHBOARD_BG_GRADIENT_FROM,
  PLAY_DASHBOARD_BG_GRADIENT_TO,
  PLAY_FILTER_BORDER,
  type QuickStat
} from '../mock-data'

interface IQuickStatsGridProps {
  stats: QuickStat[]
}

const ICON_BY_KEY: Record<QuickStat['iconKey'], FunctionComponent<SVGProps<SVGSVGElement>>> = {
  training: StatTrainingIcon,
  teams: StatTeamsIcon,
  completion: StatCompletionIcon,
  upcoming: StatUpcomingIcon
}

const QuickStatsGrid = ({ stats }: IQuickStatsGridProps) => {
  return (
    <SimpleGrid columns={{ base: 2, md: 4 }} spacing="8px">
      {stats.map(stat => {
        const Icon = ICON_BY_KEY[stat.iconKey]
        // TBD tiles (no backend source yet): dimmed, muted value color (not the
        // accent, so it doesn't read as a real number), and a hover tooltip.
        const isTbd = Boolean(stat.tbd)
        const valueColor = isTbd ? 'rgba(255,255,255,0.45)' : stat.accent
        const iconBg = isTbd ? 'rgba(255,255,255,0.06)' : `${stat.accent}1F`

        const tile = (
          <Box
            bgGradient={`linear(to-b, ${PLAY_DASHBOARD_BG_GRADIENT_FROM}, ${PLAY_DASHBOARD_BG_GRADIENT_TO})`}
            border="1px solid"
            borderColor={PLAY_FILTER_BORDER}
            borderRadius="12px"
            p={{ base: '12px', md: '16px' }}
            opacity={isTbd ? 0.55 : 1}
            cursor={isTbd ? 'help' : 'default'}
          >
            <VStack spacing="4px" align="flex-start">
              <Box
                w="28px"
                h="28px"
                borderRadius="8px"
                bg={iconBg}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Icon width={16} height={16} />
              </Box>
              <Text color={valueColor} fontSize="24px" fontWeight={700} m={0} lineHeight="24px">
                {stat.value}
              </Text>
              <HStack spacing="4px" align="baseline" minW={0} w="full">
                <Text
                  color="#F2F7FA"
                  fontSize="12px"
                  fontWeight={500}
                  lineHeight="16px"
                  textTransform="uppercase"
                  m={0}
                  flexShrink={0}
                >
                  {stat.label}
                </Text>
                <Text color="#ADB2BF" fontSize="8px" lineHeight="12px" m={0} noOfLines={1}>
                  {stat.delta}
                </Text>
              </HStack>
            </VStack>
          </Box>
        )

        return isTbd ? (
          <Tooltip
            key={stat.id}
            label="Coming soon — not available yet"
            hasArrow
            placement="top"
            bg="#1f2a3d"
            color="white"
            fontSize="12px"
            borderRadius="8px"
          >
            {tile}
          </Tooltip>
        ) : (
          cloneElement(tile, { key: stat.id })
        )
      })}
    </SimpleGrid>
  )
}

export default QuickStatsGrid
